/**
 * Builds the TanStack Start `head()` payload from a resolved meta record.
 *
 * Every public route file calls `seoHead()` so the server HTML carries a
 * unique <title>, description, canonical, og:* and (where defined) JSON-LD
 * without waiting for JavaScript. <PageMeta> no longer emits these tags —
 * duplicating them client-side would give crawlers two canonicals.
 */
import { resolveRouteMeta, SITE_NAME, SITE_URL, type ResolvedMeta } from "./routeMeta";

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface HeadPayload {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
  scripts?: Array<Record<string, string>>;
}

export function buildHead(m: ResolvedMeta): HeadPayload {
  const image = m.image ?? DEFAULT_OG_IMAGE;

  const meta: Array<Record<string, string>> = [
    { title: m.title },
    { name: "description", content: m.description },
    { property: "og:title", content: m.ogTitle },
    { property: "og:description", content: m.ogDescription },
    { property: "og:type", content: m.ogType },
    { property: "og:url", content: m.canonical },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: m.ogTitle },
    { name: "twitter:description", content: m.ogDescription },
    { name: "twitter:image", content: image },
  ];

  if (m.noIndex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  } else {
    meta.push({ name: "robots", content: "index, follow, max-image-preview:large" });
  }

  const payload: HeadPayload = {
    meta,
    links: m.noIndex ? [] : [{ rel: "canonical", href: m.canonical }],
  };

  if (m.jsonLd) {
    payload.scripts = [
      { type: "application/ld+json", children: JSON.stringify(m.jsonLd) },
    ];
  }

  return payload;
}

/** Convenience for static routes: resolve by pathname, then build. */
export function seoHead(pathname: string): HeadPayload {
  return buildHead(resolveRouteMeta(pathname));
}
